// shared/fiveLeague.js
// クイズ ワン ワード（協力クイズゲーム）用の共通定数・型定義
// 注: 内部変数名は互換性のため旧名称(FIVE_LEAGUE, FL_*)を維持

/* ───────── ゲームモード ───────── */
export const FL_GAME_MODE = {
  ONEWORD: 'oneword',   // ワンワード（従来モード）
  CHAIN: 'chain',       // アンサーチェーン（新モード）
};

/* ───────── ゲーム設定 ───────── */
export const FIVE_LEAGUE = {
  // 参加人数制限
  MIN_PLAYERS: 2,
  MAX_PLAYERS: 6,

  // ラウンド設定
  MAX_QUESTIONS: 10,          // 1ラウンド最大10問

  // タイマー設定（秒）
  ANSWER_TIME_SEC: 45,        // 回答受付時間（デフォルト）
  REVEAL_COUNTDOWN_SEC: 5,    // オープン前カウントダウン
  REVEAL_HOLD_SEC: 6,         // 結果表示時間（TTS読み上げ含む）→短縮
  SOLUTION_HOLD_SEC: 4,       // 正解単語表示時間（TTS読み上げ含む）→短縮

  // 候補表示
  MAX_CANDIDATES: 3,          // 手書き認識の最大候補数

  // ニックネーム
  NICKNAME_MAX_LENGTH: 12,
};

/* ───────── アンサーチェーン設定 ───────── */
export const CHAIN_SETTINGS = {
  // 回答数オプション
  ANSWER_COUNT_5: 5,
  ANSWER_COUNT_10: 10,

  // 制限時間（秒）
  TIME_LIMIT_5: 30,   // 5問版: 30秒
  TIME_LIMIT_10: 60,  // 10問版: 60秒

  // 入力モード
  INPUT_VOICE: 'voice',     // 音声認識（デフォルト）
  INPUT_TEXT: 'text',       // テキスト入力
  INPUT_KEYBOARD: 'keyboard', // キーボード入力

  // テキスト入力の最大文字数
  MAX_TEXT_LENGTH: 10,
};

/* ───────── フェーズ定義（ワンワード）───────── */
export const FL_PHASE = {
  READING: 'reading',                   // 問題読み上げ中
  ANSWERING: 'answering',               // 回答受付中
  REVEAL_COUNTDOWN: 'reveal_countdown', // 5秒カウントダウン
  REVEALED: 'revealed',                 // 結果表示中（赤/青）
  SOLUTION: 'solution',                 // 正解単語表示
  END_ROUND: 'end_round',               // ラウンド終了
};

/* ───────── フェーズ定義（チェーン）───────── */
export const CHAIN_PHASE = {
  ORDER: 'order',           // 順番決め
  READING: 'reading',       // 問題読み上げ中
  ANSWERING: 'answering',   // 回答中（ターン制）
  CORRECT: 'correct',       // 正解演出
  WRONG: 'wrong',           // 不正解演出
  TIMEOUT: 'timeout',       // タイムアウト（隕石落下）
  COMPLETE: 'complete',     // 全問正解
  RESULT: 'result',         // 結果表示
};

/* ───────── 文字種（script）───────── */
export const FL_SCRIPT = {
  HIRAGANA: 'hiragana',
  KATAKANA: 'katakana',
  KANJI: 'kanji',
  NUMBER: 'number',
  ALPHABET: 'alphabet',
};

// 文字種の日本語表示名（TTS読み上げにも使用）
export const FL_SCRIPT_LABELS = {
  [FL_SCRIPT.HIRAGANA]: 'ひらがな',
  [FL_SCRIPT.KATAKANA]: 'カタカナ',
  [FL_SCRIPT.KANJI]: '漢字',
  [FL_SCRIPT.NUMBER]: '半角数字',
  [FL_SCRIPT.ALPHABET]: 'アルファベットおおもじ',
};

/* ───────── 難易度定義 ───────── */
export const FL_DIFFICULTY = {
  1: { label: '★☆☆☆☆', name: 'かんたん' },
  2: { label: '★★☆☆☆', name: 'ふつう' },
  3: { label: '★★★☆☆', name: 'むずかしい' },
  4: { label: '★★★★☆', name: '激ムズ' },
  5: { label: '★★★★★', name: '超難問' },
  // 6問目以降は難易度5でループ
  6: { label: '★★★★★', name: '超難問' },
  7: { label: '★★★★★', name: '鬼難問' },
  8: { label: '★★★★★', name: '鬼難問' },
  9: { label: '★★★★★', name: '神難問' },
  10: { label: '★★★★★', name: '神難問' },
};

/* ───────── ルーム状態 ───────── */
export const FL_ROOM_STATUS = {
  WAITING: 'waiting',   // 待機中
  PLAYING: 'playing',   // ゲーム中
  FINISHED: 'finished', // 終了
};

/* ───────── プレイヤーロール ───────── */
export const FL_ROLE = {
  DISPLAY: 'display',       // TV進行画面（操作なし）
  CONTROLLER: 'controller', // 入力端末（スマホ）
  HYBRID: 'hybrid',         // Display + Controller
};

/* ───────── 結果色 ───────── */
export const FL_COLORS = {
  CORRECT_BG: '#d32f2f',    // 正解：赤背景
  INCORRECT_BG: '#1976d2',  // 不正解：青背景
  TEXT_ON_COLOR: '#ffffff', // 色背景上のテキスト：白
  SLOT_EMPTY: '#333333',    // 空枠の色
  COUNTDOWN_TEXT: '#ffffff',
};

/* ───────── UI定数 ───────── */
export const FL_UI = {
  SLOT_SIZE: 80,            // 文字枠のサイズ（px）
  SLOT_GAP: 12,             // 枠間のギャップ
  CANVAS_SIZE: 250,         // 手書きキャンバスサイズ
  FONT_SIZE_SLOT: 48,       // 枠内文字サイズ
  FONT_SIZE_COUNTDOWN: 120, // カウントダウン数字サイズ
};

/* ───────── アニメーション時間（ms）───────── */
export const FL_ANIM = {
  REVEAL_FADE_IN: 300,      // オープン時のフェードイン
  REVEAL_SCALE: 200,        // オープン時のスケール
  SOLUTION_SLIDE_IN: 400,   // 正解表示のスライドイン
  COUNTDOWN_TICK: 1000,     // カウントダウン1秒
};

/* ───────── 定型文 ───────── */
export const FL_PHRASES = {
  wait: ['よろしく！', '準備OK！', '待って！', 'がんばろう！', '楽しもう！'],
  playing: ['難しい…', '惜しい！', 'ナイス！', 'がんばれ！', '確定した！'],
  result: ['ありがとう！', '楽しかった！', 'もう一回！', '惜しかった！', 'すごい！'],
};

/* ───────── ユーティリティ関数 ───────── */

/**
 * 文字種に基づいて入力を正規化
 * @param {string} char - 入力文字
 * @param {string} script - 文字種
 * @returns {string} - 正規化された文字
 */
export function normalizeChar(char, script) {
  if (!char) return '';
  let c = String(char).trim();

  switch (script) {
    case FL_SCRIPT.ALPHABET:
      // 全角アルファベットを半角に変換し、大文字に統一
      c = c.replace(/[Ａ-Ｚａ-ｚ]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
      return c.toUpperCase();
    case FL_SCRIPT.NUMBER:
      // 全角数字を半角に
      return c.replace(/[０-９]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
    default:
      return c;
  }
}

/**
 * 文字が指定の文字種に適合するかチェック
 * @param {string} char - チェック対象文字
 * @param {string} script - 文字種
 * @returns {boolean}
 */
export function isValidCharForScript(char, script) {
  if (!char || char.length !== 1) return false;

  switch (script) {
    case FL_SCRIPT.HIRAGANA:
      return /^[\u3040-\u309F]$/.test(char);
    case FL_SCRIPT.KATAKANA:
      return /^[\u30A0-\u30FF]$/.test(char);
    case FL_SCRIPT.KANJI:
      // CJK統合漢字の基本範囲
      return /^[\u4E00-\u9FAF]$/.test(char);
    case FL_SCRIPT.NUMBER:
      return /^[0-9０-９]$/.test(char);
    case FL_SCRIPT.ALPHABET:
      return /^[A-Za-zＡ-Ｚａ-ｚ]$/.test(char);
    default:
      return true;
  }
}

/**
 * プレイヤー数から出題する問題の文字数を取得
 * @param {number} playerCount - プレイヤー数
 * @returns {number} - 問題の文字数（= プレイヤー数）
 */
export function getQuestionLength(playerCount) {
  return Math.max(FIVE_LEAGUE.MIN_PLAYERS, Math.min(FIVE_LEAGUE.MAX_PLAYERS, playerCount));
}

/**
 * 担当文字位置の表示文字列を生成
 * @param {number} index - 0-based index
 * @returns {string} - 例: "3文字目"
 */
export function getSlotLabel(index) {
  return `${index + 1}文字目`;
}

/**
 * ルームコードを生成（6文字の英数字）
 * @returns {string}
 */
export function generateRoomCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // 紛らわしい文字を除外
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * 次のフェーズを取得
 * @param {string} currentPhase - 現在のフェーズ
 * @param {boolean} isPerfect - 全員正解かどうか
 * @param {number} questionIndex - 現在の問題インデックス（0-based）
 * @returns {string} - 次のフェーズ
 */
export function getNextPhase(currentPhase, isPerfect, questionIndex) {
  switch (currentPhase) {
    case FL_PHASE.ANSWERING:
      return FL_PHASE.REVEAL_COUNTDOWN;
    case FL_PHASE.REVEAL_COUNTDOWN:
      return FL_PHASE.REVEALED;
    case FL_PHASE.REVEALED:
      return FL_PHASE.SOLUTION;
    case FL_PHASE.SOLUTION:
      // 不正解 or 最終問題 → ラウンド終了
      if (!isPerfect || questionIndex >= FIVE_LEAGUE.MAX_QUESTIONS - 1) {
        return FL_PHASE.END_ROUND;
      }
      // 正解かつ最大問題数未満 → 次の問題へ
      return FL_PHASE.READING;
    default:
      return FL_PHASE.END_ROUND;
  }
}

/**
 * デフォルトのルーム設定を生成
 * @returns {Object}
 */
export function getDefaultRoomConfig() {
  return {
    maxQuestions: FIVE_LEAGUE.MAX_QUESTIONS,
    answerTimeSec: FIVE_LEAGUE.ANSWER_TIME_SEC,
    revealCountdownSec: FIVE_LEAGUE.REVEAL_COUNTDOWN_SEC,
    revealHoldSec: FIVE_LEAGUE.REVEAL_HOLD_SEC,
    solutionHoldSec: FIVE_LEAGUE.SOLUTION_HOLD_SEC,
  };
}

/**
 * プレイヤーIDを生成
 * @returns {string}
 */
export function generatePlayerId() {
  return `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * ローカルストレージからプレイヤーIDを取得または生成
 * @returns {string}
 */
export function getOrCreatePlayerId() {
  const key = 'five_league_player_id';
  let playerId = localStorage.getItem(key);
  if (!playerId) {
    playerId = generatePlayerId();
    localStorage.setItem(key, playerId);
  }
  return playerId;
}

/**
 * ローカルストレージからニックネームを取得
 * @returns {string|null}
 */
export function getSavedNickname() {
  return localStorage.getItem('five_league_nickname');
}

/**
 * ニックネームをローカルストレージに保存
 * @param {string} nickname
 */
export function saveNickname(nickname) {
  localStorage.setItem('five_league_nickname', nickname);
}

/* ───────── チェーンモード用ユーティリティ ───────── */

/**
 * 折り返しターン順を生成
 * @param {number} playerCount - プレイヤー数
 * @param {number} answerCount - 回答数（5 or 10）
 * @returns {number[]} - スロットインデックスの配列
 *
 * 例:
 * - 3人・5問: [0,1,2,2,1]
 * - 3人・10問: [0,1,2,2,1,0,0,1,2,2]
 * - 4人・5問: [0,1,2,3,3]
 * - 4人・10問: [0,1,2,3,3,2,1,0,0,1]
 */
export function generateChainTurnOrder(playerCount, answerCount) {
  const order = [];
  let forward = true;
  let pos = 0;

  for (let i = 0; i < answerCount; i++) {
    order.push(pos);

    if (forward) {
      pos++;
      if (pos >= playerCount) {
        pos = playerCount - 1;
        forward = false;
      }
    } else {
      pos--;
      if (pos < 0) {
        pos = 0;
        forward = true;
      }
    }
  }
  return order;
}

/**
 * チェーンモードのデフォルト設定を生成
 * @param {number} answerCount - 回答数（5 or 10）
 * @returns {Object}
 */
export function getChainDefaultConfig(answerCount = 5) {
  return {
    answerCount,
    timeLimit: answerCount === 10 ? CHAIN_SETTINGS.TIME_LIMIT_10 : CHAIN_SETTINGS.TIME_LIMIT_5,
    inputMode: CHAIN_SETTINGS.INPUT_VOICE,
  };
}

/**
 * カタカナをひらがなに変換
 * @param {string} str - 入力文字列
 * @returns {string} - ひらがな化した文字列
 */
export function katakanaToHiragana(str) {
  return str.replace(/[\u30A1-\u30F6]/g, match =>
    String.fromCharCode(match.charCodeAt(0) - 0x60)
  );
}

/**
 * 回答を正規化（読み判定用）
 * @param {string} answer - 入力回答
 * @returns {string} - 正規化した回答（ひらがな、小文字、トリム済み）
 */
export function normalizeChainAnswer(answer) {
  if (!answer) return '';
  let normalized = answer.trim().toLowerCase();
  // カタカナをひらがなに
  normalized = katakanaToHiragana(normalized);
  // 長音記号を統一
  normalized = normalized.replace(/[ー－—]/g, 'ー');
  return normalized;
}
